import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Stack, Fade } from '@mui/material'
import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'
import LoadingBackdrop from '../../../../shared/components/LoadingBackdrop/LoadingBackdrop'
import MetricsCards from '../../../../shared/components/MetricsCards'
import TppElementSupportSection from './components/TppElementSupportSection'
import useTppImpactMetrics from './useTppImpactMetrics'
import { useTppColumns } from './useTppColumns'
import { useGetTppIdQuery } from '../../../TppView/hooks/useGetTppIdQuery'
import TppGanttChart from '../../../TppView/components/TppGanttChart'
import { useAuth0 } from '../../../../contexts/auth0-context'
import callAPI from '../../../../API/callSecureAPI'
import endpoints from '../../../../API/endpoints'
import { Tpp } from '../../../TppView/types'

const TppImpactTab = ({}: {
    selectedScenarioSlug?: string | null
    onScenarioChange?: (scenarioSlug: string) => void
} = {}) => {
    // @ts-expect-error store is not typed properly
    const strategyId = useSelector((state) => state.strategy?._id)
    const { getTokenSilently, isAuthenticated, isLoading: authLoading } = useAuth0()

    const { data: tppId, isLoading: tppIdLoading } = useGetTppIdQuery(strategyId)
    const { data: columns, isLoading: columnsLoading } = useTppColumns(tppId)
    const { cards, isLoading: cardsLoading } = useTppImpactMetrics()

    const canFetchTpp = !!tppId && !!getTokenSilently && !!isAuthenticated && !authLoading
    const { data: tpp, isLoading: tppLoading } = useQuery<Tpp>(
        ['tpp', tppId],
        async () => {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            const response = await callAPI(`${endpoints.getTPP.url}/${tppId}`, endpoints.getTPP.method, token)
            return response.data as Tpp
        },
        { enabled: canFetchTpp }
    )

    const [selectedColumnId, setSelectedColumnId] = useState<string | null>(null)
    const [drillDownSectionId, setDrillDownSectionId] = useState<string | null>(null)
    const [highlightedOnly, setHighlightedOnly] = useState(false)

    // Reset drill-down when column changes
    const handleColumnChange = useCallback((columnId: string) => {
        setSelectedColumnId(columnId)
        setDrillDownSectionId(null)
    }, [])

    // Filter out archived columns and sort by TPP column order
    const activeColumns = useMemo(() => {
        if (!columns) return []
        const filtered = columns.filter((col) => !col.archived).map((col) => ({ _id: col._id, title: col.title }))
        if (!tpp?.columnIds) return filtered
        return filtered.sort((a, b) => tpp.columnIds.indexOf(a._id) - tpp.columnIds.indexOf(b._id))
    }, [columns, tpp])

    // Set initial column when columns load
    useEffect(() => {
        if (activeColumns.length > 0 && !selectedColumnId) {
            setSelectedColumnId(activeColumns[0]._id)
        }
    }, [activeColumns, selectedColumnId])

    const isLoading = tppIdLoading || columnsLoading || tppLoading

    if (isLoading) {
        return <LoadingBackdrop loading title="Metrics" subtitle="Loading TrPP Impact Metrics" />
    }

    return (
        <Fade in timeout={800}>
            <Stack direction="column" gap={3}>
                <TppElementSupportSection
                    columns={activeColumns}
                    selectedColumnId={selectedColumnId}
                    onColumnChange={handleColumnChange}
                    sectionIds={tpp?.sectionIds}
                    drillDownSectionId={drillDownSectionId}
                    onDrillDownChange={setDrillDownSectionId}
                    highlightedOnly={highlightedOnly}
                    onHighlightedOnlyChange={setHighlightedOnly}
                />

                <MetricsCards cards={cards} />

                <TppGanttChart
                    tppId={tppId}
                    selectedColumnId={selectedColumnId}
                    drillDownSectionId={drillDownSectionId}
                    highlightedOnly={highlightedOnly}
                />
            </Stack>
        </Fade>
    )
}

export default TppImpactTab
